/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { t } from '@sportninja/common/i18n'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import type { PlayerStatus } from '@sportninja/common/features/team/player/utils/playerStatus'
import { formatSuspensionDates } from '@sportninja/common/features/team/player/utils/formatSuspensionDates'
import StatusChip from 'src/components/ts-components/StatusChip'
import { paragraphXSmall } from 'src/components/css'
import { PLAYER_STATUS_DISPLAY } from '../constants/playerStatusDisplay'

const translate = t as (key: string, options?: unknown) => string

interface PlayerStatusCellProps {
  status: PlayerStatus | null
}

/**
 * A roster player's Status: a pill, plus the suspension's dates under it.
 * Shared by the Team screen's roster table and the Operations roster grid so
 * both read the same. Renders nothing when there is no status.
 */
const PlayerStatusCell = ({ status }: PlayerStatusCellProps) => {
  'use memo'

  if (!status) {
    return null
  }

  const display = PLAYER_STATUS_DISPLAY[status.kind]
  const dates =
    status.kind === 'injured'
      ? ''
      : formatSuspensionDates(
          status,
          capitalizeWords(translate('common:indefinitely'))
        )

  return (
    <div css={cellStyle}>
      <StatusChip tone={display.tone}>
        {capitalizeWords(translate(display.labelKey))}
      </StatusChip>
      {dates && <span css={datesStyle}>{dates}</span>}
    </div>
  )
}

const cellStyle = css`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`

const datesStyle = css`
  ${paragraphXSmall}
  color: ${colors.NEUTRAL_50};
  white-space: nowrap;
`

export default PlayerStatusCell
